import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle
} from "@/components/ui/dialog";
import { WorkflowCreationWizard } from "./WorkflowCreationWizard";
import { WorkflowDetailView } from "./WorkflowDetailView";
import { useWorkflows, useDeleteWorkflow } from "@/hooks/useWorkflows";
import {
  Plus,
  Search,
  Trash2,
  Eye,
  Settings,
  Workflow,
} from "lucide-react";

export const WorkflowManagement: React.FC = () => {
  const [showWizard, setShowWizard] = useState(false);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(
    null
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState<string | null>(null);

  const { data: workflows = [], isLoading } = useWorkflows();
  const deleteWorkflowMutation = useDeleteWorkflow();

  const handleDeleteClick = (workflowId: string) => {
    setWorkflowToDelete(workflowId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (workflowToDelete) {
      deleteWorkflowMutation.mutate(workflowToDelete);
      setDeleteDialogOpen(false);
      setWorkflowToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setWorkflowToDelete(null);
  };

  const filteredWorkflows = workflows.filter(
    (workflow) =>
      workflow.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (workflow.description &&
        workflow.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <p className="text-gray-600">載入工作流程資料中...</p>
        </div>
      </div>
    );
  }

  if (selectedWorkflowId) {
    return (
      <WorkflowDetailView
        workflowId={selectedWorkflowId}
        onBack={() => setSelectedWorkflowId(null)}
      />
    );
  }

  if (showWizard) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => setShowWizard(false)}>
            ← 返回工作流程管理
          </Button>
        </div>
        <WorkflowCreationWizard onSuccess={() => setShowWizard(false)} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 頁面標題和操作 */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Workflow className="h-6 w-6" />
            工作流程管理
          </h2>
          <p className="text-muted-foreground mt-1">
            管理公司的工作流程，包含階段設定和部門配置
          </p>
        </div>
        <Button onClick={() => setShowWizard(true)}>
          <Plus className="h-4 w-4 mr-2" />
          新增工作流程
        </Button>
      </div>

      {/* 搜尋和篩選 */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="搜尋工作流程..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button variant="outline">
              <Settings className="h-4 w-4 mr-2" />
              篩選
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 工作流程列表 */}
      <div className="grid gap-4">
        {filteredWorkflows.map((workflow) => (
          <Card key={workflow.id} className="hover:shadow-md transition-shadow">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CardTitle className="text-lg">{workflow.name}</CardTitle>
                  <Badge variant={workflow.isActive ? "default" : "secondary"}>
                    {workflow.isActive ? "啟用" : "停用"}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedWorkflowId(workflow.id)}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleDeleteClick(workflow.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">{workflow.description}</p>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">階段數量</span>
                  <div className="font-medium">
                    {workflow.workflowSteps?.length || 0} 個階段
                  </div>
                </div>
                <div>
                  <span className="text-gray-500">涉及部門</span>
                  <div className="font-medium">
                    {(() => {
                      // Calculate unique departments across all steps
                      const uniqueDepartments = new Set();
                      workflow.workflowSteps?.forEach((step) => {
                        step.workflowStepDepartments?.forEach((dept) => {
                          uniqueDepartments.add(dept.departmentId);
                        });
                      });
                      return uniqueDepartments.size;
                    })()}{" "}
                    個部門
                  </div>
                </div>
                <div>
                  <span className="text-gray-500">建立時間</span>
                  <div className="font-medium">
                    {workflow.createdAt
                      ? new Date(workflow.createdAt).toLocaleDateString("zh-TW")
                      : "未知"}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* 空狀態 */}
      {filteredWorkflows.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Workflow className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {searchTerm ? "找不到符合的工作流程" : "尚未建立任何工作流程"}
            </h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm
                ? "請嘗試其他搜尋關鍵字"
                : "開始建立您的第一個工作流程"}
            </p>
            {!searchTerm && (
              <Button onClick={() => setShowWizard(true)}>
                <Plus className="h-4 w-4 mr-2" />
                新增工作流程
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>刪除工作流程</DialogTitle>
            <DialogDescription>
              您確定要刪除這個工作流程嗎？此操作無法復原。
              {workflowToDelete && (
                <>
                  <br />
                  <br />
                  <strong>
                    工作流程：{workflows.find(w => w.id === workflowToDelete)?.name}
                  </strong>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleDeleteCancel}>
              取消
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteConfirm}
              disabled={deleteWorkflowMutation.isPending}
            >
              {deleteWorkflowMutation.isPending ? '刪除中...' : '確認刪除'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
